import { ConfigProvider } from 'antd';
import arEG from 'antd/locale/ar_EG';
import enUS from 'antd/locale/en_US';
import { lazy, Suspense, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Navigate, Route, Routes } from 'react-router-dom';
import { getThemeMode } from '../../store/Theme/ThemeSlice';
import { loadLanguage } from '../../store/Language/LanguageSlice';
import Loading from '../Loading/Loading';
import { hasPermission } from '../../utilities/authentication';

const RequirePermission = ({ permission, children }) => {
  if (permission && !hasPermission(permission)) {
    const Forbidden = lazy(() => import('../../pages/Backoffice/Forbidden'));
    return (
      <Suspense fallback={<Loading />}>
        <Forbidden />
      </Suspense>
    );
  }
  return children;
};

const AppRoutes = () => {
  const dispatch = useDispatch();
  const { theme } = useSelector((state) => state.theme);
  const { language } = useSelector((state) => state.language);

  const cairoFont = "'Cairo', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Ubuntu', sans-serif";
  const brandPrimary = '#1FBAC2';
  const brandPrimaryHover = '#1BA8B0';
  const brandPrimaryActive = '#17969D';

  const lightTheme = {
    token: {
      colorPrimary: brandPrimary,
      colorPrimaryHover: brandPrimaryHover,
      colorPrimaryActive: brandPrimaryActive,
      colorBgBase: '#fff',
      colorTextQuaternary: '#828181',
      ...(language === 'ar' ? { fontFamily: cairoFont } : {}),
    },
  };

  const darkTheme = {
    token: {
      colorPrimary: brandPrimary,
      colorPrimaryHover: brandPrimaryHover,
      colorPrimaryActive: brandPrimaryActive,
      colorBgBase: '#001529',
      colorPrimaryBg: '#001529',
      colorText: 'white',
      colorTextQuaternary: '#aeb0af',
      controlOutlineWidth: '0',
      colorErrorBg: '#ff4d4f',
      ...(language === 'ar' ? { fontFamily: cairoFont } : {}),
    },
  };

  useEffect(() => {
    dispatch(getThemeMode());
    dispatch(loadLanguage());
  }, [dispatch]);

  const PrivateRoute = lazy(() => import('../Routes/PrivateRoute'));
  const BackofficeDashboard = lazy(() => import('../../pages/Backoffice/Dashboard'));
  const BackofficeVendors = lazy(() => import('../../pages/Backoffice/Vendors'));
  const BackofficeVendorForm = lazy(() => import('../../pages/Backoffice/VendorForm'));
  const BackofficeVendorEdit = lazy(() => import('../../pages/Backoffice/VendorEdit'));
  const BackofficeSubscriptionPlans = lazy(() => import('../../pages/Backoffice/SubscriptionPlans'));
  const BackofficeSubscriptions = lazy(() => import('../../pages/Backoffice/Subscriptions'));
  const PlatformClients = lazy(() => import('../../pages/Backoffice/PlatformClients'));
  const PlatformOrders = lazy(() => import('../../pages/Backoffice/PlatformOrders'));
  const PlatformUsers = lazy(() => import('../../pages/Backoffice/PlatformUsers'));
  const Profile = lazy(() => import('../../pages/Profile/Profile'));
  const Activities = lazy(() => import('../../pages/Activities/Activities'));

  return (
    <>
      <Suspense fallback={<Loading />}>
        <Routes>
          <Route
            path="/"
            element={
              <ConfigProvider locale={language === 'ar' ? arEG : enUS} direction={language === 'ar' ? 'rtl' : 'ltr'} theme={theme === 'dark' ? darkTheme : lightTheme}>
                <PrivateRoute />
              </ConfigProvider>
            }
          >
            <Route index element={<Navigate to="/dashboard" replace />} />
            <Route path="dashboard" element={<BackofficeDashboard />} />
            <Route path="vendors" element={<BackofficeVendors />} />
            <Route path="vendors/create" element={<BackofficeVendorForm />} />
            <Route path="vendors/:id/edit" element={<BackofficeVendorEdit />} />
            <Route path="subscription-plans" element={<BackofficeSubscriptionPlans />} />
            <Route path="subscriptions" element={<BackofficeSubscriptions />} />
            <Route path="clients" element={<PlatformClients />} />
            <Route path="orders" element={<PlatformOrders />} />
            <Route path="platform/users" element={<PlatformUsers />} />
            <Route path="profile" element={<Profile />} />
            <Route
              path="activities"
              element={
                <RequirePermission permission="view_activity_logs">
                  <Activities />
                </RequirePermission>
              }
            />
            <Route path="*" element={<Navigate to="/dashboard" replace />} />
          </Route>
        </Routes>
      </Suspense>
    </>
  );
};

export default AppRoutes;
